import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BellRinging, ChatCircleText, EnvelopeSimple, PhoneCall } from "@phosphor-icons/react";
import api, { formatError } from "@/lib/api";
import { Modal, Field, Select } from "@/components/Modal";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { inr } from "@/pages/Customers";

const BUCKET = { overdue: ["Overdue", "bg-red-50 text-red-700 border-red-200"], due_today: ["Due today", "bg-amber-50 text-amber-800 border-amber-200"],
  upcoming: ["Due in 3 days", "bg-blue-50 text-blue-700 border-blue-200"], no_due_date: ["No due date", "bg-slate-100 text-slate-600 border-slate-200"],
  later: ["Later", "bg-slate-50 text-slate-500 border-slate-200"] };

function FollowUp({ r, statuses, onClose, onSaved }) {
  const [f, setF] = useState({ status: r.follow_up_status, note: "", next_follow_up: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/customers/${r.customer_id}/payment-followup`, { ...f, next_follow_up: f.next_follow_up || null }); toast.success("Follow-up saved"); onClose(); onSaved(); window.dispatchEvent(new Event("followup-saved")); }
    catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal open onOpenChange={onClose} title={`Payment follow-up · ${r.customer_name}`} onSubmit={submit} submitting={busy} testid="followup-modal">
      <Select label="Follow-up status" value={f.status} onChange={(v) => setF({ ...f, status: v })} options={statuses} testid="fu-status" />
      <Field label="Follow-up note" value={f.note} onChange={(v) => setF({ ...f, note: v })} testid="fu-note" />
      <Field label="Next follow-up date" type="date" value={f.next_follow_up} onChange={(v) => setF({ ...f, next_follow_up: v })} testid="fu-next" />
    </Modal>
  );
}

function Row({ r, onFollow }) {
  const [label, cls] = BUCKET[r.bucket];
  return (
    <div className="grid md:grid-cols-[1.4fr_1fr_1fr_auto] gap-3 items-center py-3 border-b last:border-0" data-testid={`reminder-${r.customer_id}`}>
      <div>
        <Link to={`/customers/${r.customer_id}`} className="font-semibold hover:text-primary">{r.customer_name}</Link>
        <p className="text-xs text-muted-foreground">{[r.phone || "No phone", r.assigned_employees || "Unassigned"].join(" · ")}</p>
      </div>
      <div className="text-sm">
        <p>Payable {inr(r.total_payable)} · Received {inr(r.verified_received)}</p>
        <p className="font-bold" data-testid={`reminder-balance-${r.customer_id}`}>Balance {inr(r.remaining_balance)}</p>
      </div>
      <div className="text-sm space-y-1">
        <span className={`inline-block text-[11px] border rounded-full px-2 py-0.5 ${cls}`} data-testid={`reminder-bucket-${r.customer_id}`}>{label}{r.due_date ? ` · ${r.due_date}` : ""}</span>
        <div className="flex items-center gap-1"><StatusBadge status={r.follow_up_status} />{r.next_follow_up && <span className="text-[11px] text-muted-foreground">next {r.next_follow_up}</span>}</div>
        {r.follow_up_note && <p className="text-[11px] text-muted-foreground truncate max-w-[240px]" title={r.follow_up_note}>{r.follow_up_by}: {r.follow_up_note}</p>}
      </div>
      <div className="flex gap-1">
        {onFollow && <Button size="sm" variant="outline" onClick={() => onFollow(r)} data-testid={`reminder-followup-${r.customer_id}`}><ChatCircleText size={14} className="mr-1" />Follow-up</Button>}
        <Link to={`/communication/compose?customer=${r.customer_id}&template=payment_reminder`}><Button size="sm" variant="ghost" data-testid={`reminder-compose-${r.customer_id}`}><EnvelopeSimple size={14} /></Button></Link>
      </div>
    </div>
  );
}

export function CallList() {
  const { user, can } = useAuth();
  const [d, setD] = useState(null);
  const [scope, setScope] = useState("mine");
  const [fu, setFu] = useState(null);
  const admin = ["super_admin", "admin", "accounts"].includes(user?.role);
  const load = () => api.get(`/follow-up-calls?scope=${scope}`).then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => {
    if (!can("customers:view")) return undefined;
    load();
    window.addEventListener("followup-saved", load);
    return () => window.removeEventListener("followup-saved", load);
  }, [scope]);
  if (!d) return null;
  return (
    <div className="border border-border bg-white rounded-2xl p-5 card-soft mb-6" data-testid="call-list">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2"><PhoneCall size={20} className="text-primary" /><h3 className="font-semibold">Call today · {d.items.length} follow-up{d.items.length === 1 ? "" : "s"}</h3></div>
        {admin && <div className="flex rounded-full border p-0.5 text-xs">{["mine", "all"].map((s) => <button key={s} onClick={() => setScope(s)} data-testid={`call-scope-${s}`} className={`px-3 py-1 rounded-full ${scope === s ? "bg-slate-900 text-white" : ""}`}>{s === "mine" ? "My calls" : "All employees"}</button>)}</div>}
      </div>
      {d.items.length === 0 ? <p className="text-sm text-muted-foreground py-2" data-testid="call-list-empty">No follow-up calls scheduled for today.</p> : d.items.map((r) => (
        <div key={r.customer_id} className="flex flex-wrap items-center justify-between gap-3 py-3 border-b last:border-0" data-testid={`call-${r.customer_id}`}>
          <div className="min-w-[180px]">
            <Link to={`/customers/${r.customer_id}`} className="font-semibold hover:text-primary">{r.customer_name}</Link>
            {r.phone ? <a href={`tel:${r.phone}`} className="block text-sm text-primary" data-testid={`call-phone-${r.customer_id}`}>{r.phone}</a> : <span className="block text-sm text-muted-foreground">No phone</span>}
          </div>
          <div className="text-sm flex-1 min-w-[200px]">
            <p>Balance <strong>{inr(r.remaining_balance)}</strong> · <span className={r.overdue ? "text-red-600 font-semibold" : ""} data-testid={`call-date-${r.customer_id}`}>{r.overdue ? `Missed (${r.next_follow_up})` : "Today"}</span></p>
            <p className="text-xs text-muted-foreground truncate max-w-[420px]">{r.follow_up_status}{r.follow_up_note ? ` — ${r.follow_up_note}` : ""}{admin && scope === "all" ? ` · ${r.follow_up_by}` : ""}</p>
          </div>
          {(can("customers:manage") || can("payments:record")) && <Button size="sm" variant="outline" onClick={() => setFu(r)} data-testid={`call-log-${r.customer_id}`}><ChatCircleText size={14} className="mr-1" />Log call</Button>}
        </div>))}
      {fu && <FollowUp r={fu} statuses={d.follow_up_statuses} onClose={() => setFu(null)} onSaved={load} />}
    </div>
  );
}

export function PaymentReminders() {
  const { user, can } = useAuth();
  const [d, setD] = useState(null);
  const [scope, setScope] = useState("mine");
  const [fu, setFu] = useState(null);
  const ops = ["sales", "allocation"].includes(user?.role);
  const load = () => api.get(`/payment-reminders?scope=${scope}`).then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => {
    if (!can("customers:view")) return undefined;
    load();
    window.addEventListener("followup-saved", load);
    return () => window.removeEventListener("followup-saved", load);
  }, [scope]);
  if (!d) return null;
  const actionable = d.items.filter((r) => r.bucket !== "later");
  return (
    <div className="border border-border bg-white rounded-2xl p-5 card-soft mb-6" data-testid="payment-reminders">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2"><BellRinging size={20} className="text-primary" /><h3 className="font-semibold">Payment due reminders · {d.today}</h3></div>
        <div className="flex items-center gap-2 text-xs">
          <span data-testid="reminder-counts">{d.counts.overdue} overdue · {d.counts.due_today} due today · {d.counts.upcoming} in 3 days · {d.counts.no_due_date} no due date · {inr(d.total_outstanding)} outstanding{d.counts.later ? ` · ${d.counts.later} due later` : ""}</span>
          {ops && <div className="flex rounded-full border p-0.5">{["mine", "all"].map((s) => <button key={s} onClick={() => setScope(s)} data-testid={`reminder-scope-${s}`} className={`px-3 py-1 rounded-full capitalize ${scope === s ? "bg-slate-900 text-white" : ""}`}>{s === "mine" ? "My customers" : "All"}</button>)}</div>}
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mb-2">Reminders are for internal follow-up only. No payment reminder is ever emailed to a customer automatically.</p>
      {actionable.length === 0 ? <p className="text-sm text-muted-foreground py-3" data-testid="reminders-empty">No payments due right now.</p> :
        actionable.slice(0, 25).map((r) => <Row key={r.customer_id} r={r} onFollow={can("customers:manage") || can("payments:record") ? setFu : null} />)}
      {fu && <FollowUp r={fu} statuses={d.follow_up_statuses} onClose={() => setFu(null)} onSaved={load} />}
    </div>
  );
}
